import {
  AlertCircle,
  Check,
  Clock,
  CreditCard,
  RefreshCw,
  Search,
} from "lucide-react";
import { FlowButton, FlowCard, FlowPanel, FlowRow } from "./FlowPanel";

interface PaymentSuccessViewProps {
  rentalData: {
    rental?: {
      id?: string;
      pick_up?: string;
      return?: string;
      guests?: {
        adults: number;
        children: number;
        infants: number;
        pets: number;
      };
      calculated_total_amount?: string | number;
      rentable?: {
        name?: string;
      };
    };
    payment?: {
      status?: string;
      amount?: string | number;
      card?: {
        brand?: string;
        last4?: string;
        exp_month?: string | number;
        exp_year?: string | number;
      };
    };
  };
  onTryDifferentPayment: () => void;
  onRetry: () => void;
  onBackToSearch?: () => void;
}

const formatCurrency = (amount: string | number) => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(numAmount);
};

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

/** Same short form the emails use: first 8 characters, uppercase. */
const shortBookingId = (id?: string) =>
  id ? id.slice(0, 8).toUpperCase() : "N/A";

const formatGuests = (
  guests: NonNullable<
    PaymentSuccessViewProps["rentalData"]["rental"]
  >["guests"],
) => {
  if (!guests) return "N/A";
  const parts = [`${guests.adults} adults`];
  if (guests.children > 0) parts.push(`${guests.children} children`);
  if (guests.infants > 0) parts.push(`${guests.infants} infants`);
  if (guests.pets > 0) parts.push(`${guests.pets} pets`);
  return parts.join(", ");
};

const STATUS_COPY: Record<
  string,
  {
    kicker: string;
    title: string;
    message: string;
    pill: string;
    label: string;
  }
> = {
  succeeded: {
    kicker: "Payment received",
    title: "You're booked.",
    message:
      "Your reservation is confirmed. You can find all the details below.",
    pill: "bg-[#e6f4f2] text-[#137136]",
    label: "Paid",
  },
  failed: {
    kicker: "Payment issue",
    title: "Payment Processing Issue",
    message:
      "We encountered an issue processing your payment. Don't worry - your reservation details are safe and we're here to help you complete your booking.",
    pill: "bg-[#fdecea] text-[#b42318]",
    label: "Failed",
  },
  pending: {
    kicker: "Payment pending",
    title: "Payment Processing",
    message: "Your payment is being processed. Please wait for confirmation.",
    pill: "bg-[#f6e8d6] text-[#7a4505]",
    label: "Pending",
  },
  requires_action: {
    kicker: "Action required",
    title: "Payment Authentication Required",
    message:
      "Your payment requires additional authentication to complete the reservation.",
    pill: "bg-[#f6e8d6] text-[#7a4505]",
    label: "Requires action",
  },
};

const UNKNOWN_COPY = {
  kicker: "Payment status",
  title: "Payment Status",
  message: "Please check your payment status.",
  pill: "bg-[#f4f6f6] text-[#5a616b]",
  label: "Unknown",
};

function StatusIcon({ status }: { status: string }) {
  if (status === "succeeded") {
    return (
      <div
        aria-hidden="true"
        className="flex h-14 w-14 items-center justify-center rounded-full bg-[#e6f4f2]"
      >
        <Check className="h-7 w-7 text-[#009080]" strokeWidth={2.6} />
      </div>
    );
  }
  const Icon =
    status === "pending" || status === "requires_action" ? Clock : AlertCircle;
  const tone = status === "failed" ? "text-[#b42318]" : "text-[#7a4505]";
  return (
    <div
      aria-hidden="true"
      className="flex h-14 w-14 items-center justify-center rounded-full bg-white ring-1 ring-[#e6e7e8]"
    >
      <Icon className={`h-7 w-7 ${tone}`} />
    </div>
  );
}

export function PaymentSuccessView({
  rentalData,
  onTryDifferentPayment,
  onRetry,
  onBackToSearch,
}: PaymentSuccessViewProps) {
  const rawPaymentStatus = rentalData?.payment?.status || "unknown";
  // Keep: a rental with an id is a confirmed booking whatever the payment says.
  const paymentStatus = rentalData?.rental?.id ? "succeeded" : rawPaymentStatus;
  const copy = STATUS_COPY[paymentStatus] ?? UNKNOWN_COPY;
  const card = rentalData?.payment?.card;

  return (
    <FlowPanel
      kicker={copy.kicker}
      title={copy.title}
      description={copy.message}
      icon={<StatusIcon status={paymentStatus} />}
    >
      {rentalData && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FlowCard title="Reservation">
            <FlowRow
              label="Vehicle"
              value={rentalData.rental?.rentable?.name || "N/A"}
            />
            <FlowRow
              label="Booking ID"
              value={shortBookingId(rentalData.rental?.id)}
              mono
            />
            <FlowRow
              label="Pick up"
              value={
                rentalData.rental?.pick_up
                  ? formatDate(rentalData.rental.pick_up)
                  : "N/A"
              }
            />
            <FlowRow
              label="Return"
              value={
                rentalData.rental?.return
                  ? formatDate(rentalData.rental.return)
                  : "N/A"
              }
            />
            <FlowRow
              label="Guests"
              value={formatGuests(rentalData.rental?.guests)}
            />
          </FlowCard>

          <FlowCard title="Payment">
            {rentalData.payment?.amount && (
              <div className="mb-2 font-mono text-3xl font-semibold tracking-tight text-[#06131b]">
                {formatCurrency(rentalData.payment.amount)}
              </div>
            )}
            <FlowRow
              label="Status"
              value={
                <span
                  className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${copy.pill}`}
                >
                  {copy.label}
                </span>
              }
            />
            {card && (
              <FlowRow
                label="Method"
                value={`${card.brand ? card.brand.toUpperCase() : "Card"} •••• ${card.last4 ?? ""}`}
              />
            )}
            <p className="mt-3 text-xs text-[#5a616b]">
              Your payment details are encrypted and handled by Stripe.
            </p>
          </FlowCard>
        </div>
      )}

      {paymentStatus !== "succeeded" && (
        <div className="mt-8 flex flex-col items-center gap-4">
          {paymentStatus === "failed" ? (
            <>
              <FlowButton onClick={onTryDifferentPayment}>
                <CreditCard className="h-5 w-5" aria-hidden="true" />
                Try Different Payment Method
              </FlowButton>
              <button
                type="button"
                onClick={onRetry}
                className="text-sm font-medium text-[#00695e] underline"
              >
                Refresh Status
              </button>
            </>
          ) : (
            <FlowButton onClick={onRetry}>
              <RefreshCw className="h-5 w-5" aria-hidden="true" />
              Refresh Status
            </FlowButton>
          )}
        </div>
      )}

      {paymentStatus === "succeeded" && (
        <div className="mt-4 rounded-xl border border-[#e6e7e8] bg-white p-5">
          <h2 className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5a616b]">
            What happens next
          </h2>
          <ol className="space-y-3 text-sm text-[#454c57]">
            {[
              [
                "Now.",
                "Your confirmation email arrives with the booking details.",
              ],
              [
                "24 hours before.",
                "Pickup instructions and contact information are sent to your email.",
              ],
              [
                "Questions?",
                "Contact the fleet and have your booking ID ready.",
              ],
            ].map(([lead, text], i) => (
              <li key={lead} className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#e6f4f2] font-mono text-xs font-semibold text-[#00695e]"
                >
                  {i + 1}
                </span>
                <span>
                  <b className="font-semibold text-[#06131b]">{lead}</b> {text}
                </span>
              </li>
            ))}
          </ol>
          {onBackToSearch && (
            <div className="mt-5 text-center">
              <FlowButton onClick={onBackToSearch}>
                <Search className="h-5 w-5" aria-hidden="true" />
                Search for Another Vehicle
              </FlowButton>
            </div>
          )}
        </div>
      )}
    </FlowPanel>
  );
}
